import { useEffect, useId, useRef, useState } from 'react'
import { Etichetta } from '../../componenti/base.tsx'
import type * as posta from '../../servizi/posta.ts'
import '../../stile-posta-caselle.css'

/**
 * Selettore delle caselle in cima a Posta (decisione di Davide del 2/11/2026), come quello degli account
 * di Outlook: chi ha piu' indirizzi vede quanti messaggi ha da leggere in ciascuno e sceglie quale aprire,
 * senza mescolarli. «Tutte le caselle» resta una voce, non la scelta d'ingresso. Sul telefono, dove la
 * colonna delle caselle e' un pannello a parte, e' il modo piu' rapido di cambiare casella.
 *
 * E' un comando che apre un elenco di comandi (schema «disclosure»): la voce aperta ha aria-current.
 */

/** Il numero dei messaggi da leggere, se ce ne sono. */
export function ConteggioDaLeggere({ n }: { n: number | null | undefined }) {
  if (!n) return null
  return <span className="conteggio da-leggere" aria-label={n === 1 ? '1 da leggere' : `${n} da leggere`}>{n}</span>
}

function Etichette({ c }: { c: posta.CasellaWebmail }) {
  return (
    <>
      {c.ambito === 'gruppo' && <Etichetta tono="accento">gruppo</Etichetta>}
      {c.ambito === 'progetto' && <Etichetta tono="accento">progetto</Etichetta>}
      {c.livello === 'legge' && <Etichetta>sola lettura</Etichetta>}
      {c.ambito === 'organizzazione' && <Etichetta>ente</Etichetta>}
      {c.supervisione && <Etichetta tono="critico">supervisione</Etichetta>}
    </>
  )
}

export function SelettoreCaselle({ caselle, caselleMie, vista, casellaId, scegliCasella, scegliTutte }: {
  caselle: posta.CasellaWebmail[]
  caselleMie: posta.CasellaWebmail[]
  vista: posta.VistaPosta['modo']
  casellaId: string | null
  scegliCasella: (id: string) => void
  scegliTutte: () => void
}) {
  const [aperto, setAperto] = useState(false)
  const contenitore = useRef<HTMLDivElement>(null)
  const idElenco = useId()

  // Si chiude con un tocco fuori o con Esc, e il fuoco torna al comando.
  useEffect(() => {
    if (!aperto) return
    const fuori = (e: MouseEvent) => { if (!contenitore.current?.contains(e.target as Node)) setAperto(false) }
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setAperto(false)
      contenitore.current?.querySelector<HTMLButtonElement>('.selettore-caselle-comando')?.focus()
    }
    document.addEventListener('mousedown', fuori)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', esc) }
  }, [aperto])

  if (caselle.length < 2) return null
  const tutte = vista === 'tutte'
  const scelta = tutte ? null : caselle.find((c) => c.id === casellaId) ?? null
  const totale = caselleMie.reduce((t, c) => t + (c.non_letti ?? 0), 0)
  // Da leggere nelle altre caselle: il motivo per cui si apre il selettore.
  const altrove = caselle.filter((c) => c.id !== scelta?.id).reduce((t, c) => t + (c.non_letti ?? 0), 0)

  function scegli(f: () => void) {
    f()
    setAperto(false)
  }

  return (
    <div className="selettore-caselle" ref={contenitore}>
      <button type="button" className="selettore-caselle-comando" aria-expanded={aperto}
              aria-controls={idElenco} onClick={() => setAperto((a) => !a)}>
        <span className="selettore-caselle-scelta">
          <span className="selettore-caselle-nome">
            {tutte ? 'Tutte le caselle' : scelta ? (scelta.nome_visualizzato || scelta.indirizzo) : 'Scegli una casella'}
            <ConteggioDaLeggere n={tutte ? totale : scelta?.non_letti} />
          </span>
          {scelta && <span className="selettore-caselle-indirizzo">{scelta.indirizzo}</span>}
        </span>
        {!tutte && altrove > 0 && (
          <span className="selettore-caselle-altrove">{altrove} da leggere nelle altre caselle</span>
        )}
        <span className="selettore-caselle-freccia" aria-hidden="true">▾</span>
      </button>
      {aperto && (
        <ul className="selettore-caselle-elenco" id={idElenco} aria-label="Caselle">
          {caselleMie.length > 1 && (
            <li>
              <button type="button" className={tutte ? 'attiva' : ''} aria-current={tutte || undefined}
                      onClick={() => scegli(scegliTutte)}>
                <span className="selettore-caselle-nome">Tutte le caselle <ConteggioDaLeggere n={totale} /></span>
                <span className="selettore-caselle-indirizzo">le tue caselle in una sola lista</span>
              </button>
            </li>
          )}
          {caselle.map((c) => {
            const attiva = !tutte && c.id === casellaId
            return (
              <li key={c.id}>
                <button type="button" className={attiva ? 'attiva' : ''} aria-current={attiva || undefined}
                        onClick={() => scegli(() => scegliCasella(c.id))}>
                  <span className="selettore-caselle-nome">
                    {c.nome_visualizzato || c.indirizzo}
                    <Etichette c={c} />
                    <ConteggioDaLeggere n={c.non_letti} />
                  </span>
                  <span className="selettore-caselle-indirizzo">{c.indirizzo}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
